import React, {
  useEffect,
  RefObject,
  useMemo,
  useState,
} from 'react';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  useFormContext,
} from 'react-hook-form';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import isNil from 'lodash/isNil';
import Controller from 'core/components/FormController';
import DateTimeIntervalPicker from 'core/components/DateTimePicker/DateTimeIntervalPicker';
import {
  fromStartAndDuration,
  MIN_APPOINTMENT_DATE_ERROR_MESSAGE,
  validateDateTimeIntervalValue,
  validateMinAppointmentDateTime,
} from 'core/components/DateTimePicker/dateTimeInterval';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  formatISODateTime,
  formatDateOnly,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import {
  CalendarEvent,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/index';
import {
  useGetCalendarEvents,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  convertToCalendarViewEvents,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import WeekDateStrip from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/WeekDateStrip';
import EventCalendarSchedule from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/EventCalendarSchedule';
import clsx from 'clsx';
import Spinner from 'pages/Dashboard/components/Spinner';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import map from 'lodash/map';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  isAppointment: boolean;
  isTimeBlock: boolean;
  data: CalendarEvent;
  dateInputRef: RefObject<HTMLInputElement>;
};

function DateEditBanner() {
  return (
    <div className="flex items-start gap-2 bg-populate-light-blue-2 rounded px-3 py-2 mx-4">
      <InfoOutlinedIcon className="!w-3.5 !h-3.5 mt-0.5 shrink-0 text-secondary" />
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-semibold text-gray-700">Pick a new time</span>
        <span className="text-xs text-gray-500">
          Edit the date and time, or click on the calendar below to reschedule.
        </span>
      </div>
    </div>
  );
}

export default function DateEditPanel({
  open,
  onClose,
  onSubmit,
  isAppointment,
  isTimeBlock,
  data,
  dateInputRef,
}: Props) {
  const {
    control,
    formState: { isValid },
    watch,
    setValue,
    trigger,
  } = useFormContext<AppointmentForm>();
  const eventDateTime = watch('eventDateTime');
  const userInfo = useUserInfo();
  const [eventDateTimeInputError, setEventDateTimeInputError] = useState<string | null>(null);

  useEffect(() => {
    trigger('eventDateTime');
  }, [eventDateTimeInputError, trigger]);

  const {
    data: allEvents,
    isLoading: loadingCalendarEvents,
  } = useGetCalendarEvents(
    formatDateOnly(eventDateTime?.startTime ?? ''),
    data?.userId ?? 0,
    open && !isNil(eventDateTime?.startTime),
  );

  const normalizedEvents = useMemo(() => convertToCalendarViewEvents(
    allEvents as CalendarEvent[],
    new Set(map(allEvents, 'id')),
    data?.objectId,
  ).map((event) => ({
    ...event,
    id: `${event?.id}_reschedule`,
    title: event?.objectId !== data?.objectId ? event.title ?? '' : `Editing: ${event.title ?? ''}`,
    cssClass: clsx(event.cssClass, {
      'referenced-event-for-reschedule': event?.objectId !== data?.objectId,
      'current-event-for-reschedule': event?.objectId === data?.objectId,
    }),
  })), [allEvents, data]);

  const handleClose = () => {
    setValue('eventDateTime', fromStartAndDuration(data?.start, data?.durationInMinutes), {
      shouldTouch: false,
      shouldDirty: false,
    });
    onClose();
  };

  const currentDate = useMemo(() => {
    const start = eventDateTime?.startTime;

    return start?.isValid() ? dayjs(start).format('ddd, MMM D') : '';
  }, [eventDateTime]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      hideBackdrop
      style={{ height: 'calc(100% - 0.875rem)' }}
      classes={{
        root: 'detailed-entity-drawer has-extension !overflow-[initial]',
        paper: '!w-[640px] !overflow-hidden !pointer-events-auto',
      }}
      disableEnforceFocus
      disableScrollLock
      disableRestoreFocus
      ModalProps={{ style: { pointerEvents: 'none' } }}
    >
      <div className="flex flex-col h-full gap-3 nw-edit-appointment-date-time overflow-hidden !w-[640px]">
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-xl font-semibold">
            Reschedule
            {' '}
            {isAppointment ? 'Appointment' : `${isTimeBlock ? 'Time Block' : 'Availability'}`}
          </h3>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon className="!w-5 !h-5 !text-inherit" />
          </IconButton>
        </div>
        <div className="flex flex-col gap-3 flex-1 min-h-0">
          <div className="flex flex-col gap-4 px-4 overflow-hidden">
            <WeekDateStrip />
            <Controller
              control={control}
              name="eventDateTime"
              rules={{
                required: true,
                validate: {
                  inputError: () => isNil(eventDateTimeInputError) || eventDateTimeInputError,
                  invalidValue: (value) => (
                    validateDateTimeIntervalValue(value)
                    || 'Invalid date or duration'
                  ),
                  minAppointmentDate: (value) => (
                    validateMinAppointmentDateTime(value)
                    || MIN_APPOINTMENT_DATE_ERROR_MESSAGE
                  ),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="event-date-range"
                  error={!isNil(error)}
                  errorText={error?.message}
                  showErrorText={false}
                  className="w-full text-gray-500 text-xs relative z-10"
                  label="Date & Time"
                >
                  <DateTimeIntervalPicker
                    {...field}
                    dateInputRef={dateInputRef}
                    timezone={userInfo?.organization?.timeZoneId ?? undefined}
                    onInputErrorChange={setEventDateTimeInputError}
                    variant={isAppointment ? 'durationPicker' : 'timepicker'}
                  />
                </Wrapper>
              )}
            />
          </div>
          <DateEditBanner />
          <div className={clsx(
            'flex-1 min-h-0 overflow-hidden relative z-10',
            ' border border-gray-100 rounded-md mx-4',
            {
              'opacity-50': loadingCalendarEvents,
            },
          )}
          >
            <div className="sticky top-0 z-10 p-2 bg-gray-100 border-b border-gray-200">
              <p className="font-semibold text-sm">
                {currentDate}
              </p>
            </div>
            <EventCalendarSchedule
              selectedDate={formatISODateTime(dayjs(eventDateTime?.startTime))}
              isLoading={loadingCalendarEvents}
              {...(loadingCalendarEvents
                ? { data: [] }
                : { event: data, data: normalizedEvents })}
            />
          </div>
          {loadingCalendarEvents && (
            <div className="absolute inset-0 flex items-center justify-center z-50">
              <Spinner size={48} />
            </div>
          )}
        </div>
        <div className="flex gap-3 shrink-0 p-4 justify-end">
          <Button
            variant="outlined"
            onClick={handleClose}
            className="nw-cancel-update-datetime-button"
          >
            Close
          </Button>
          <Button
            onClick={onSubmit}
            color="primary"
            variant="contained"
            disabled={!isValid}
            className="nw-update-datetime-button"
          >
            Update
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
